import { get, isEqual, set, unset } from "lodash";

export type DynamicFormData = Record<string, unknown>;
export type RenamedDynamicFormKey = {
  oldKey: string;
  newKey: string;
};

type FlattenedKeyMap = Record<string, unknown>;

const isObjectLike = (value: unknown): value is DynamicFormData =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isPathPrefix = (path: string, prefix: string) =>
  path === prefix || path.startsWith(`${prefix}.`);

const parentPath = (path: string) => path.split(".").slice(0, -1).join(".");

const replacePathPrefix = (
  path: string,
  oldPrefix: string,
  newPrefix: string
) => {
  if (path === oldPrefix) return newPrefix;
  return `${newPrefix}.${path.slice(oldPrefix.length + 1)}`;
};

export const normalizeDynamicFormData = (
  data: DynamicFormData | string | null | undefined
): DynamicFormData => {
  let normalizedData: unknown = data;

  while (typeof normalizedData === "string") {
    if (!normalizedData.trim()) return {};

    try {
      normalizedData = JSON.parse(normalizedData);
    } catch {
      return {};
    }
  }

  return isObjectLike(normalizedData) ? normalizedData : {};
};

export const cloneDynamicFormData = (
  data: DynamicFormData | string | null | undefined
): DynamicFormData =>
  JSON.parse(JSON.stringify(normalizeDynamicFormData(data)));

export const extractDynamicFormKeys = (
  obj: DynamicFormData | null | undefined,
  prefix = ""
): FlattenedKeyMap => {
  if (!isObjectLike(obj)) return {};

  let keys: FlattenedKeyMap = {};
  Object.entries(obj).forEach(([key, value]) => {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    keys[fullPath] = value;

    if (isObjectLike(value)) {
      keys = { ...keys, ...extractDynamicFormKeys(value, fullPath) };
    }
  });

  return keys;
};

const shortestKey = (keys: string[]): string => {
  if (!keys.length) return "";
  return keys.reduce((shortest, key) =>
    key.length < shortest.length ? key : shortest
  );
};

const detectBranchRename = (
  oldKeys: FlattenedKeyMap,
  newKeys: FlattenedKeyMap,
  removedKeys: string[],
  addedKeys: string[]
): RenamedDynamicFormKey | null => {
  // The root of a branch rename is the shortest removed/added key,
  // since all other keys must be descendants of it.
  const oldRoot = shortestKey(removedKeys);
  const newRoot = shortestKey(addedKeys);

  if (!removedKeys.every((key) => isPathPrefix(key, oldRoot))) return null;
  if (!addedKeys.every((key) => isPathPrefix(key, newRoot))) return null;
  if (parentPath(oldRoot) !== parentPath(newRoot)) return null;

  const transformedKeySet = new Set(
    removedKeys.map((key) => replacePathPrefix(key, oldRoot, newRoot))
  );
  if (transformedKeySet.size !== addedKeys.length) return null;
  if (!addedKeys.every((key) => transformedKeySet.has(key))) return null;

  const hasValueMismatch = removedKeys.some((oldKey) => {
    const mappedKey = replacePathPrefix(oldKey, oldRoot, newRoot);
    return !isEqual(oldKeys[oldKey], newKeys[mappedKey]);
  });

  return hasValueMismatch ? null : { oldKey: oldRoot, newKey: newRoot };
};

export const detectRenamedKey = (
  oldData: DynamicFormData | null | undefined,
  newData: DynamicFormData | null | undefined
): RenamedDynamicFormKey | null => {
  const oldKeys = extractDynamicFormKeys(oldData);
  const newKeys = extractDynamicFormKeys(newData);

  const oldKeyPaths = Object.keys(oldKeys);
  const newKeyPaths = Object.keys(newKeys);

  const oldKeySet = new Set(oldKeyPaths);
  const newKeySet = new Set(newKeyPaths);

  const removedKeys = oldKeyPaths.filter((key) => !newKeySet.has(key));
  const addedKeys = newKeyPaths.filter((key) => !oldKeySet.has(key));

  if (!removedKeys.length || removedKeys.length !== addedKeys.length) {
    return null;
  }

  if (removedKeys.length === 1 && addedKeys.length === 1) {
    const oldKey = removedKeys[0];
    const newKey = addedKeys[0];
    if (isEqual(oldKeys[oldKey], newKeys[newKey])) {
      return { oldKey, newKey };
    }
    return null;
  }

  return detectBranchRename(oldKeys, newKeys, removedKeys, addedKeys);
};

export const transformDataWithRenamedKey = (
  data: DynamicFormData,
  oldKey: string,
  newKey: string
): DynamicFormData => {
  const nextData = cloneDynamicFormData(data);
  const oldValue = get(data, oldKey);

  if (typeof oldValue !== "undefined") {
    unset(nextData, oldKey);
    set(nextData, newKey, oldValue);
  }

  return nextData;
};
